import { useState, useEffect, useCallback } from 'react';
import api, { formatoFecha } from '@/lib/api';
import { getWhatsAppLink } from '@/lib/whatsapp';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, Plus, MessageCircle, Phone, Check, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';

const ESTADOS_REC = {
  pendiente: { label: 'Pendiente', clase: 'bg-amber-500/20 text-amber-400 border-amber-800' },
  enviado: { label: 'Enviado', clase: 'bg-blue-500/20 text-blue-400 border-blue-800' },
  completado: { label: 'Completado', clase: 'bg-green-500/20 text-green-400 border-green-800' },
};

function formatearTelWhatsApp(telefono) {
  if (!telefono) return '';
  let tel = telefono.replace(/[\s\-()+']/g, '');
  if (tel.startsWith('52')) return tel;
  if (tel.startsWith('1') && tel.length === 11) return tel;
  if (tel.length === 10) return '52' + tel;
  return tel;
}

function generarLinkWhatsApp(telefono, rec) {
  const tel = formatearTelWhatsApp(telefono);
  if (!tel) return '#';
  const msg = `Hola! Le escribimos de su taller de confianza.\n\nLe recordamos que su vehiculo ${rec.vehiculo_info || ''} tiene programado: ${rec.tipo_servicio}${rec.fecha_recordatorio ? `\nFecha sugerida: ${rec.fecha_recordatorio}` : ''}${rec.kilometraje_proximo ? `\nKilometraje: ${rec.kilometraje_proximo} km` : ''}\n\nLe invitamos a agendar su cita. Quedamos a sus ordenes!`;
  return getWhatsAppLink(tel, msg);
}

function diasRestantes(fecha) {
  if (!fecha) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const target = new Date(fecha);
  return Math.ceil((target - hoy) / (1000 * 60 * 60 * 24));
}

export default function Recordatorios() {
  const [recordatorios, setRecordatorios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [form, setForm] = useState({
    cliente_id: '', vehiculo_id: '', tipo_servicio: '', fecha_recordatorio: '', kilometraje_proximo: '', notas: ''
  });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/recordatorios', { params: { estado: filtroEstado === 'todos' ? '' : filtroEstado } });
      setRecordatorios(res.data);
    } catch { toast.error('Error al cargar recordatorios'); }
    finally { setCargando(false); }
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDialog = async () => {
    try {
      const [cRes, vRes] = await Promise.all([
        api.get('/clientes?busqueda='),
        api.get('/vehiculos?busqueda=&cliente_id=')
      ]);
      setClientes(cRes.data);
      setVehiculos(vRes.data);
    } catch { /* ignore */ }
    setForm({ cliente_id: '', vehiculo_id: '', tipo_servicio: '', fecha_recordatorio: '', kilometraje_proximo: '', notas: '' });
    setDialog(true);
  };

  const crear = async () => {
    if (!form.cliente_id || !form.tipo_servicio) {
      toast.error('Cliente y tipo de servicio son obligatorios');
      return;
    }
    setGuardando(true);
    try {
      await api.post('/recordatorios', form);
      toast.success('Recordatorio creado');
      setDialog(false);
      cargar();
    } catch { toast.error('Error al crear recordatorio'); }
    finally { setGuardando(false); }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      await api.patch(`/recordatorios/${id}/estado`, { estado });
      toast.success(`Marcado como ${estado}`);
      cargar();
    } catch { toast.error('Error al actualizar'); }
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`/recordatorios/${id}`);
      toast.success('Recordatorio eliminado');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const vehiculosCliente = vehiculos.filter(v => v.cliente_id === form.cliente_id);

  // Separate by urgency
  const vencidos = recordatorios.filter(r => r.estado === 'pendiente' && diasRestantes(r.fecha_recordatorio) !== null && diasRestantes(r.fecha_recordatorio) < 0);
  const proximos = recordatorios.filter(r => r.estado === 'pendiente' && diasRestantes(r.fecha_recordatorio) !== null && diasRestantes(r.fecha_recordatorio) >= 0 && diasRestantes(r.fecha_recordatorio) <= 7);
  const futuros = recordatorios.filter(r => r.estado === 'pendiente' && (diasRestantes(r.fecha_recordatorio) === null || diasRestantes(r.fecha_recordatorio) > 7));
  const otros = recordatorios.filter(r => r.estado !== 'pendiente');

  const renderGroup = (titulo, items, colorTitulo) => {
    if (items.length === 0) return null;
    return (
      <div key={titulo}>
        <h2 className={`text-sm font-medium uppercase tracking-wider mb-3 ${colorTitulo}`}>{titulo} ({items.length})</h2>
        <div className="space-y-2 mb-6">
          {items.map(r => {
            const est = ESTADOS_REC[r.estado] || ESTADOS_REC.pendiente;
            const dias = diasRestantes(r.fecha_recordatorio);
            return (
              <Card key={r.id} className="bg-slate-900/40 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`recordatorio-card-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-slate-200">{r.tipo_servicio}</span>
                        <Badge className={est.clase + ' text-xs'}>{est.label}</Badge>
                        {dias !== null && r.estado === 'pendiente' && (
                          <span className={`text-xs ${dias < 0 ? 'text-red-400' : dias <= 7 ? 'text-amber-400' : 'text-slate-500'}`}>
                            {dias < 0 ? `Vencido hace ${Math.abs(dias)}d` : dias === 0 ? 'Hoy' : `En ${dias}d`}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-300">{r.cliente_nombre}</p>
                      <p className="text-xs text-slate-500">
                        {r.vehiculo_info}
                        {r.fecha_recordatorio && ` · ${formatoFecha(r.fecha_recordatorio)}`}
                        {r.kilometraje_proximo && ` · ${r.kilometraje_proximo} km`}
                      </p>
                      {r.notas && <p className="text-xs text-slate-600 mt-1">{r.notas}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 flex-wrap">
                      {r.cliente_telefono && r.estado === 'pendiente' && (
                        <a href={generarLinkWhatsApp(r.cliente_telefono, r)}
                          target="_blank" rel="noopener noreferrer"
                          onClick={() => cambiarEstado(r.id, 'enviado')}
                          className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`whatsapp-rec-${r.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      )}
                      {r.cliente_telefono && r.estado === 'pendiente' && (
                        <a href={`tel:${r.cliente_telefono}`}
                          className="inline-flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`llamar-rec-${r.id}`}>
                          <Phone className="w-3.5 h-3.5" /> Llamar
                        </a>
                      )}
                      {r.estado === 'pendiente' && (
                        <Button variant="ghost" size="sm" onClick={() => cambiarEstado(r.id, 'enviado')}
                          className="text-blue-400 hover:text-blue-300 text-xs" data-testid={`enviar-rec-${r.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Enviado
                        </Button>
                      )}
                      {r.estado !== 'completado' && (
                        <Button variant="ghost" size="sm" onClick={() => cambiarEstado(r.id, 'completado')}
                          className="text-green-400 hover:text-green-300 text-xs" data-testid={`completar-rec-${r.id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Completado
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => eliminar(r.id)}
                        className="text-red-400 hover:text-red-300 text-xs" data-testid={`eliminar-rec-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recordatorios-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Bell className="w-8 h-8 text-amber-500" /> Recordatorios
        </h1>
        <div className="flex items-center gap-3">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="filtro-estado-rec">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="todos" className="text-slate-200">Todos</SelectItem>
              <SelectItem value="pendiente" className="text-slate-200">Pendiente</SelectItem>
              <SelectItem value="enviado" className="text-slate-200">Enviado</SelectItem>
              <SelectItem value="completado" className="text-slate-200">Completado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={abrirDialog} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-recordatorio-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Recordatorio
          </Button>
        </div>
      </div>

      {recordatorios.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="py-16 text-center">
            <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay recordatorios</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {renderGroup('Vencidos', vencidos, 'text-red-400')}
          {renderGroup('Proximos (7 dias)', proximos, 'text-amber-400')}
          {renderGroup('Futuros', futuros, 'text-slate-400')}
          {renderGroup('Enviados / Completados', otros, 'text-slate-500')}
        </>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="nuevo-recordatorio-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Nuevo Recordatorio</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Cliente *</Label>
              <Select value={form.cliente_id} onValueChange={v => setForm({...form, cliente_id: v, vehiculo_id: ''})}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-cliente-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.nombre_completo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.cliente_id && vehiculosCliente.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Vehículo</Label>
                <Select value={form.vehiculo_id} onValueChange={v => setForm({...form, vehiculo_id: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-vehiculo-select">
                    <SelectValue placeholder="Seleccionar vehiculo" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {vehiculosCliente.map(v => (
                      <SelectItem key={v.id} value={v.id} className="text-slate-200">{v.marca} {v.modelo} - {v.placas}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tipo de Servicio *</Label>
              <Input value={form.tipo_servicio} onChange={e => setForm({...form, tipo_servicio: e.target.value})}
                placeholder="Ej: Cambio de aceite, Revision de frenos..."
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-tipo-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Fecha Recordatorio</Label>
                <Input type="date" value={form.fecha_recordatorio} onChange={e => setForm({...form, fecha_recordatorio: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-fecha-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Kilometraje Proximo</Label>
                <Input value={form.kilometraje_proximo} onChange={e => setForm({...form, kilometraje_proximo: e.target.value})}
                  placeholder="Ej: 50000"
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-km-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="rec-notas-input" />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crear} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-recordatorio-btn">
              {guardando ? 'Guardando...' : 'Crear Recordatorio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
